import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import api from '../lib/api';
import { patientAPI } from '../lib/api';
import { formatDate, apiErrorMessage } from '../lib/utils';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from '../components/ui/dialog';
import { DoorClosed, Eye, Search, ShieldAlert, UserCheck, RotateCcw } from 'lucide-react';
import { toast } from 'sonner';

export function ClosedCasesList() {
  const navigate = useNavigate();
  const { user, hasRole } = useAuth();
  const [cases, setCases] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [reopenModal, setReopenModal] = useState({ open: false, patient: null });
  const [reopenReason, setReopenReason] = useState('');
  const [reopening, setReopening] = useState(false);

  const canReopen = hasRole(['super_admin', 'admin', 'receptionist', 'doctor']);

  useEffect(() => { fetchClosedCases(); }, []);

  const fetchClosedCases = async () => {
    try {
      const response = await api.get('/closed-cases');
      setCases(response.data.closed_cases || []);
    } catch (error) {
      toast.error('Failed to load closed cases');
    } finally {
      setLoading(false);
    }
  };

  const handleReopen = async () => {
    if (reopenReason.trim().length < 5) {
      toast.error('Reason must be at least 5 characters');
      return;
    }
    setReopening(true);
    try {
      await patientAPI.reopenCase(reopenModal.patient.id, { reason: reopenReason.trim() });
      toast.success(`Case reopened for ${reopenModal.patient.full_name}`);
      setReopenModal({ open: false, patient: null });
      setReopenReason('');
      fetchClosedCases();
    } catch (error) {
      toast.error(apiErrorMessage(error, 'Failed to reopen case'));
    } finally {
      setReopening(false);
    }
  };

  const filtered = cases.filter(c =>
    !search || c.full_name?.toLowerCase().includes(search.toLowerCase())
    || c.patient_id?.toLowerCase().includes(search.toLowerCase())
    || c.closure_remark?.toLowerCase().includes(search.toLowerCase())
  );

  if (loading) {
    return <div className="text-center py-8 text-slate-500">Loading closed cases...</div>;
  }

  return (
    <>
      <Card data-testid="closed-cases-card">
        <CardHeader>
          <div className="flex items-center justify-between">
            <CardTitle className="text-lg font-semibold font-[Manrope] flex items-center gap-2">
              <DoorClosed className="w-5 h-5 text-slate-500" />
              Closed Cases ({cases.length})
            </CardTitle>
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
              <Input
                placeholder="Search by name, ID, or remark..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className="pl-9 w-64"
                data-testid="closed-cases-search"
              />
            </div>
          </div>
        </CardHeader>
        <CardContent>
          {filtered.length === 0 ? (
            <p className="text-center text-slate-500 py-8">
              {cases.length === 0 ? 'No closed cases yet' : 'No cases match your search'}
            </p>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full" data-testid="closed-cases-table">
                <thead>
                  <tr className="border-b border-slate-200">
                    <th className="text-left py-3 px-4 text-sm font-medium text-slate-500">Patient</th>
                    <th className="text-left py-3 px-4 text-sm font-medium text-slate-500">ID</th>
                    <th className="text-left py-3 px-4 text-sm font-medium text-slate-500">Type</th>
                    <th className="text-left py-3 px-4 text-sm font-medium text-slate-500">Doctor</th>
                    <th className="text-left py-3 px-4 text-sm font-medium text-slate-500">Closure</th>
                    <th className="text-left py-3 px-4 text-sm font-medium text-slate-500">Remark</th>
                    <th className="text-left py-3 px-4 text-sm font-medium text-slate-500">Closed On</th>
                    <th className="text-left py-3 px-4 text-sm font-medium text-slate-500">Closed By</th>
                    <th className="text-right py-3 px-4 text-sm font-medium text-slate-500">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {filtered.map((c) => (
                    <tr key={c.id} className="border-b border-slate-100 hover:bg-slate-50 transition-colors">
                      <td className="py-3 px-4">
                        <p className="font-medium text-slate-900">{c.full_name}</p>
                        {c.phone && <p className="text-xs text-slate-500">{c.phone}</p>}
                      </td>
                      <td className="py-3 px-4">
                        <code className="text-xs bg-slate-100 px-2 py-0.5 rounded">{c.patient_id}</code>
                      </td>
                      <td className="py-3 px-4">
                        <Badge className={c.patient_type === 'indoor' ? 'bg-blue-100 text-blue-800' : 'bg-green-100 text-green-800'}>
                          {c.patient_type || 'OPD'}
                        </Badge>
                      </td>
                      <td className="py-3 px-4 text-sm text-slate-600">
                        {c.assigned_doctor_name || 'Unassigned'}
                      </td>
                      <td className="py-3 px-4">
                        {c.closure_type === 'opt_out' ? (
                          <Badge className="bg-amber-100 text-amber-800 gap-1">
                            <ShieldAlert className="w-3 h-3" />
                            Opt-Out
                          </Badge>
                        ) : (
                          <Badge className="bg-emerald-100 text-emerald-800 gap-1">
                            <UserCheck className="w-3 h-3" />
                            Normal
                          </Badge>
                        )}
                      </td>
                      <td className="py-3 px-4">
                        <p className="text-sm text-slate-600 max-w-[200px] truncate" title={c.closure_remark}>
                          {c.closure_remark || '-'}
                        </p>
                      </td>
                      <td className="py-3 px-4 text-sm text-slate-600 whitespace-nowrap">
                        {c.closed_at ? formatDate(c.closed_at) : '-'}
                      </td>
                      <td className="py-3 px-4 text-sm text-slate-600">
                        {c.closed_by_name || '-'}
                      </td>
                      <td className="py-3 px-4 text-right">
                        <div className="flex items-center justify-end gap-2">
                          {canReopen && (
                            <Button
                              size="sm"
                              variant="outline"
                              className="text-emerald-600 hover:text-emerald-700 hover:bg-emerald-50 border-emerald-200"
                              onClick={() => { setReopenModal({ open: true, patient: c }); setReopenReason(''); }}
                              data-testid={`reopen-${c.id}`}
                            >
                              <RotateCcw className="w-4 h-4 mr-1" />
                              Reopen
                            </Button>
                          )}
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => navigate(`/patients/${c.id}`)}
                            data-testid={`view-closed-${c.id}`}
                          >
                            <Eye className="w-4 h-4 mr-1" />
                            Details
                          </Button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Reopen Case Modal */}
      <Dialog open={reopenModal.open} onOpenChange={(open) => { if (!open) setReopenModal({ open: false, patient: null }); }}>
        <DialogContent data-testid="reopen-case-modal">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <RotateCcw className="w-5 h-5 text-emerald-600" />
              Reopen Case
            </DialogTitle>
            <DialogDescription>
              Reopen the closed case for <strong>{reopenModal.patient?.full_name}</strong> ({reopenModal.patient?.patient_id})
            </DialogDescription>
          </DialogHeader>

          {reopenModal.patient?.closure_type === 'opt_out' && (
            <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 text-sm text-amber-800">
              <ShieldAlert className="w-4 h-4 inline mr-1" />
              This case was force-closed with opt-out. Previous remark: <em>"{reopenModal.patient?.closure_remark}"</em>
            </div>
          )}

          <div className="space-y-3 mt-2">
            <div>
              <Label htmlFor="reopen-reason">Reason for reopening <span className="text-red-500">*</span></Label>
              <Textarea
                id="reopen-reason"
                value={reopenReason}
                onChange={(e) => setReopenReason(e.target.value)}
                placeholder="e.g., Patient returned for follow-up, additional treatment needed..."
                rows={3}
                className={reopenReason.length > 0 && reopenReason.trim().length < 5 ? 'border-red-400' : ''}
                data-testid="reopen-reason-input"
              />
              {reopenReason.length > 0 && reopenReason.trim().length < 5 && (
                <p className="text-xs text-red-500 mt-1">Minimum 5 characters required</p>
              )}
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setReopenModal({ open: false, patient: null })} data-testid="cancel-reopen-btn">
              Cancel
            </Button>
            <Button
              onClick={handleReopen}
              disabled={reopening || reopenReason.trim().length < 5}
              className="bg-emerald-500 hover:bg-emerald-600"
              data-testid="confirm-reopen-btn"
            >
              {reopening ? 'Reopening...' : 'Reopen Case'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
